import { motion } from "framer-motion";
import { Star, Quote, BadgeCheck } from "lucide-react";
import { testimonials, reviewStats } from "@/data/products";

const initials = (name) =>
  name
    .replace(/^Dr\.?\s+/i, "")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Stars = ({ n, className = "" }) => (
  <div className={`flex gap-0.5 ${className}`}>
    {Array.from({ length: n }).map((_, s) => (
      <Star key={s} size={15} className="fill-[#c79a3e] text-[#c79a3e]" />
    ))}
  </div>
);

export const Testimonials = () => {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-16">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[#e85d04]">
              <span className="h-px w-8 bg-[#c79a3e]" />
              Trusted by thousands
            </span>
            <h2 className="font-serif-display text-5xl sm:text-6xl tracking-tight text-[#1a1f1b] mt-3 leading-[0.95]">
              Results people feel
            </h2>
          </div>

          {/* aggregate rating */}
          <div
            data-testid="review-aggregate"
            className="inline-flex items-center gap-4 rounded-2xl border border-[#1a1f1b]/10 bg-[#eae5dc] px-6 py-4 w-fit"
          >
            <div className="text-center">
              <div className="font-serif-display text-4xl leading-none text-[#1a1f1b]">
                {reviewStats.average.toFixed(1)}
              </div>
              <Stars n={5} className="mt-1.5 justify-center" />
            </div>
            <div className="h-10 w-px bg-[#1a1f1b]/10" />
            <div className="leading-tight">
              <div className="font-medium text-[#1a1f1b]">
                {reviewStats.count.toLocaleString()}+
              </div>
              <div className="text-xs text-[#4a524c]">{reviewStats.label}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => {
            const dark = i === 1;
            return (
              <motion.figure
                key={t.name}
                data-testid={`testimonial-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.1 }}
                className={`relative flex flex-col justify-between rounded-3xl border border-[#1a1f1b]/10 p-8 ${
                  dark ? "bg-[#1d231e] text-[#f4f1eb] md:-translate-y-6" : "bg-[#eae5dc] text-[#1a1f1b]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <Quote size={36} className={dark ? "text-[#ff8a3d]" : "text-[#e85d04]"} />
                  <Stars n={t.rating} />
                </div>
                <blockquote className="font-serif-display text-2xl leading-snug tracking-tight mt-6">
                  “{t.quote}”
                </blockquote>

                <figcaption className="mt-8 flex items-center gap-3.5">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                      dark ? "bg-[#c79a3e] text-[#1d231e]" : "bg-[#1d231e] text-[#f4f1eb]"
                    }`}
                    aria-hidden="true"
                  >
                    {initials(t.name)}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium truncate">{t.name}</span>
                      {t.verified && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-medium text-[#2f9e44]"
                          title="Verified buyer"
                        >
                          <BadgeCheck size={13} className="text-[#2f9e44]" />
                          <span className="sr-only">Verified buyer</span>
                        </span>
                      )}
                    </div>
                    <div className={`text-xs ${dark ? "text-[#f4f1eb]/60" : "text-[#4a524c]"}`}>
                      {t.role} · {t.location}
                    </div>
                    <div className={`mt-0.5 text-[11px] ${dark ? "text-[#ff8a3d]" : "text-[#b34700]"}`}>
                      Verified purchase · {t.product}
                    </div>
                  </div>
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
      </div>
    </section>
  );
};
